import { useState, FormEvent, FormEventHandler, useRef } from 'react';
import Handler from 'utils/types/Handler';
import { useDebouncedFunction } from '@instinet-analytics/core-lib';

type ConfigHookResult<T> = T & {
    onInputChange: FormEventHandler<HTMLInputElement>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onValueChange: (name: keyof T, value: any) => void;
    onToggleConfigValue: (name: keyof T) => void;
    setValues: (values: Partial<T>) => void;
};

export default function useDebouncedConfigInputs<T>(
    initialState: Partial<T>,
    onConfigChanged: Handler<Partial<T>>
): ConfigHookResult<Partial<T>> {
    const [inputs, setInputs] = useState(initialState);
    const pendingDelta = useRef<Partial<T>>({});

    const [debouncedConfigChange] = useDebouncedFunction(
        () => {
            onConfigChanged(pendingDelta.current);
            pendingDelta.current = {};
        },
        2000,
        {
            callOnUnmount: true,
        }
    );

    const setValues = (values: Partial<T>) => {
        setInputs({
            ...inputs,
            ...values,
        });
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onValueChange = (name: keyof T, value: any) => {
        setInputs({
            ...inputs,
            [name]: value,
        });

        pendingDelta.current[name] = value;

        debouncedConfigChange();
    };

    const onInputChange: FormEventHandler<HTMLInputElement> = (
        event: FormEvent<HTMLInputElement>
    ) => {
        const { name, value, type } = event.currentTarget;
        if (type === 'number') {
            const n = Number(value);
            if (!isNaN(n)) {
                onValueChange(name as keyof T, n);
                return;
            }
        }
        onValueChange(name as keyof T, value);
    };

    const onToggleConfigValue = (name: keyof T) => {
        const currentValue =
            pendingDelta.current[name] === undefined
                ? inputs[name]
                : pendingDelta.current[name];

        onValueChange(name as keyof T, !currentValue);
    };

    return {
        ...inputs,
        setValues,
        onInputChange,
        onValueChange,
        onToggleConfigValue,
    };
}
